import { routePlanAirTimeDiff, convertDurationToHours } from '@lp/features/RoutePlanner/utils';
import { DonorType, OrganType } from '@/api/apis/case';
import { RoutePlanMetrics } from '@/api/apis/mission-planner';
import { MS_IN_S } from '@/constants/date.constants';
import { RoutePlanType } from '@/types/route';
import { convertToTimezoneOffset } from '@/utils/helpers';
import { useBaseCaseDetails } from '@/hooks/useBaseCaseDetails';
import { RouteItemShell } from './RouteItemShell';
import { RouteResourcesList } from './RouteResourcesList';

type RouteItemProps = {
  routePlan: RoutePlanType;
  organType: OrganType;
  benchmarkMetrics?: RoutePlanMetrics;
  orTimeUTC: string;
  isPartialRoute?: boolean;
};

const renderBenchmarkDiff = (benchmarkMetrics: RoutePlanMetrics, routeMetrics: RoutePlanMetrics) => {
  const { timeMs, percents, isNegative } = routePlanAirTimeDiff(benchmarkMetrics, routeMetrics);

  if (!timeMs) return null;

  const percentsString = percents ? `${isNegative ? '-' : '+'}${percents}% ` : '';

  return (
    <span
      className={`w-max block py-0.5 px-1 rounded-sm text-s-sb ${isNegative ? 'bg-success-90 text-success-30' : 'bg-danger-90 text-danger-30'}`}
    >
      {`${percentsString}(${convertDurationToHours(timeMs)}h)`}
    </span>
  );
};

export const RouteItem = ({
  routePlan,
  organType,
  benchmarkMetrics,
  orTimeUTC,
  isPartialRoute = false,
}: RouteItemProps) => {
  const { donorType } = useBaseCaseDetails();

  const dryRunDuration =
    routePlan.dryRunMetrics?.totalFlightDuration || routePlan.dryRunMetrics?.totalDriveDuration || 0;

  const showDryRunTime = !isPartialRoute && donorType === DonorType.DCD && !!dryRunDuration;

  return (
    <RouteItemShell
      routePlan={routePlan}
      organType={organType}
      flightTimeBadges={
        <>
          {!!routePlan.orTimeDiffMs && (
            <span className="w-max block py-0.5 px-1 rounded-sm text-s-sb bg-base-70 text-base-10">
              {`OR ${convertToTimezoneOffset(routePlan.orTimeDiffMs)}h`}
            </span>
          )}
          {!isPartialRoute && !!benchmarkMetrics && renderBenchmarkDiff(benchmarkMetrics, routePlan.metrics)}
        </>
      }
      extraDurationRow={
        showDryRunTime && (
          <div className="flex gap-1">
            <span className="text-m-r text-base-20">Dry Run</span>
            <span className="text-m-sb text-base-5">{convertDurationToHours(dryRunDuration * MS_IN_S)}h</span>
          </div>
        )
      }
    >
      <RouteResourcesList suggestedRouteId={routePlan.id} resources={routePlan.resources} orTimeUTC={orTimeUTC} />
    </RouteItemShell>
  );
};
